import { useEffect, useState } from 'react';
import { message } from 'antd';

import { Input } from 'pages/Dialogs/styled-components/common-components';
import useTranslation from 'customHooks/translate';
import { contactUs } from 'services/auth';

import CustomDialog from './CustomDialog';

interface IProps {
  open: boolean;
  currentAccountData?: any;
  handleClose: () => void;
}

const initialValues: {
  fullName: string;
  email: string;
  subject?: string;
  message?: string;
  phone: string;
} = {
  fullName: '',
  email: '',
  subject: '',
  message: '',
  phone: '',
};

type ContactUsFormValues = typeof initialValues;

const ContactUsForm = ({ open, handleClose, currentAccountData }: IProps) => {
  const translation = useTranslation();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [contactUsDate, setContactUsDate] = useState(initialValues);

  useEffect(() => {
    if (!currentAccountData) {
      setContactUsDate(initialValues);
      return;
    }

    setContactUsDate({
      fullName: currentAccountData.fullName || '',
      email: currentAccountData.email || '',
      phone: currentAccountData.mobileNumber || '',
    });
  }, [currentAccountData, open]);

  const setOnValueChange = (event, propName, isSelect = false) => {
    event.persist && event.persist();
    setContactUsDate((prevState) => ({
      ...prevState,
      [propName]: isSelect ? event : event.target.value,
    }));
    setErrors((prev) => ({
      ...prev,
      [propName]: '',
    }));
  };

  const handleSave = () => {
    setLoading(true);
    setErrors({});

    const payload: ContactUsFormValues = {
      fullName: contactUsDate.fullName || '',
      email: contactUsDate.email || '',
      subject: contactUsDate.subject || '',
      message: contactUsDate.message || '',
      phone: contactUsDate.phone || '',
    };

    contactUs(payload)
      .then(() => {
        message.success(`${translation.messages.successSent}`);
        handleCloseModal();
      })
      .catch(
        (error: {
          response?: {
            data?: {
              Message?: string;
              errors?: {
                FullName?: string[];
                Email?: string[];
                Subject?: string[];
                Message?: string[];
                Phone?: string[];
              };
            };
          };
        }) => {
          const responseData = error.response?.data;

          responseData?.Message && message.error(responseData.Message);

          const apiErrors = responseData?.errors;
          if (apiErrors) {
            setErrors({
              fullName: apiErrors.FullName?.[0] || '',
              email: apiErrors.Email?.[0] || '',
              subject: apiErrors.Subject?.[0] || '',
              message: apiErrors.Message?.[0] || '',
              phone: apiErrors.Phone?.[0] || '',
            });
          } else if (!responseData?.Message) {
            message.error(translation.emailSendError);
          }
        }
      )
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCloseModal = () => {
    setErrors({});
    handleClose();
    setContactUsDate(initialValues);
  };

  return (
    <CustomDialog
      isLoading={loading}
      buttonTitle={translation.apply}
      open={open}
      handleClose={handleCloseModal}
      onSubmit={handleSave}
      title={translation.checkEmail.ContactLink}
    >
      <form className="flex flex-col gap-[12px]">
        <div className="flex flex-col gap-[12px]">
          <label
            className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
            htmlFor="name"
          >
            {translation.subusers.Name} *
          </label>
          <Input
            className="d-block"
            type="text"
            maxLength={100}
            onChange={(event) => setOnValueChange(event, 'fullName')}
            placeholder={translation.subusers.Name}
            value={contactUsDate?.fullName || ''}
            style={{
              borderColor: errors.fullName ? 'red' : '',
            }}
          />
          {errors.fullName && (
            <span className="text-[12px] text-red-600">{errors.fullName}</span>
          )}
        </div>
        <div className="flex flex-col gap-[12px]">
          <label
            className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
            htmlFor="name"
          >
            {translation.info.email_address} *
          </label>
          <Input
            className="d-block"
            type="email"
            maxLength={100}
            onChange={(event) => setOnValueChange(event, 'email')}
            placeholder={translation.info.email_address}
            value={contactUsDate?.email || ''}
            style={{
              borderColor: errors.email ? 'red' : '',
            }}
          />
          {errors.email && (
            <span className="text-[12px] text-red-600">{errors.email}</span>
          )}
        </div>
        <div className="flex flex-col gap-[12px]">
          <label
            className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
            htmlFor="name"
          >
            {translation.paymentMethods.Subject} *
          </label>
          <Input
            className="d-block"
            type="text"
            maxLength={100}
            onChange={(event) => setOnValueChange(event, 'subject')}
            placeholder={translation.paymentMethods.Subject}
            value={contactUsDate?.subject || ''}
            style={{
              borderColor: errors.subject ? 'red' : '',
            }}
          />
          {errors.subject && (
            <span className="text-[12px] text-red-600">{errors.subject}</span>
          )}
        </div>
        <div className="flex flex-col gap-[12px]">
          <label
            className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
            htmlFor="name"
          >
            {translation.paymentMethods.Message} *
          </label>
          <textarea
            name="description"
            placeholder={translation.paymentMethods.Message}
            value={contactUsDate?.message || ''}
            onChange={(event) => setOnValueChange(event, 'message')}
            className={`h-[150px] w-full rounded-[6px] border p-[10px] ${
              errors.message ? 'border-red-500' : 'border-[rgba(41,45,50,0.25)]'
            }`}
          />
          {errors.message && (
            <span className="text-[12px] text-red-600">{errors.message}</span>
          )}
        </div>
        <div className="flex flex-col gap-[12px]">
          <label
            className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
            htmlFor="name"
          >
            {translation.info.mobile_number}
          </label>
          <Input
            className="d-block"
            type="text"
            maxLength={20}
            onChange={(event) => setOnValueChange(event, 'phone')}
            placeholder={translation.info.mobile_number}
            value={contactUsDate?.phone || ''}
            style={{
              borderColor: errors.phone ? 'red' : '',
            }}
          />
          {errors.phone && (
            <span className="text-[12px] text-red-600">{errors.phone}</span>
          )}
        </div>
      </form>
    </CustomDialog>
  );
};

export default ContactUsForm;
